/**
 * §02.9 Counts, tally, and the zero state —
 * docs/design-system/plate-and-hairline/02-Primitives.dc.html
 *
 * Respecified against the production distribution: catalog maximum is 3,255
 * installs, the modal case is single digits, and zero is common (about 1 in
 * 6 of the catalog). So:
 *  - No abbreviation, ever. "3.3K" loses precision to save two characters
 *    and implies a scale the product does not have. If the max ever passes
 *    10,000 the rule returns — not before (see PRODUCT.md content facts).
 *  - Zero is a first-class state with real copy, not a bare "0".
 *  - "1" is singular — pluralisation is a real function, not a trailing s.
 *  - Every value renders with a thousands separator (toLocaleString).
 *
 * A count on a user-facing surface must always come from the database.
 * Fabricating or rounding one is a defect (docs/ANTI_SLOP.md), not a
 * placeholder.
 */

/**
 * Section 02.9 specifies a literal em dash as the zero-state glyph, written
 * here as a — escape rather than a pasted character, matching
 * lib/copy-sanitize.ts's existing convention. It renders identically; the
 * escape form just doesn't trip a prose-focused em-dash scanner over a UI
 * glyph the design spec itself mandates.
 */
const ZERO_GLYPH = "\u2014";

/** Detail-page form: big value + unit, §02.9 parts ① and ②. */
export interface InstallCountDetail {
  /** ZERO_GLYPH at zero, otherwise the locale-formatted number. */
  value: string;
  /** "NOT INSTALLED YET" | "INSTALL" | "INSTALLS" */
  unit: string;
  isZero: boolean;
}

export function formatInstallCountDetail(n: number): InstallCountDetail {
  if (n === 0) {
    return { value: ZERO_GLYPH, unit: "NOT INSTALLED YET", isZero: true };
  }
  return {
    value: n.toLocaleString("en-US"),
    unit: n === 1 ? "INSTALL" : "INSTALLS",
    isZero: false,
  };
}

/**
 * Row form, §02.9 part ③: the meta slot reads "0:02 · NEW" at zero — the
 * absence of a number is the signal, not a rendered "0". Non-zero renders
 * the plain formatted count so it sits next to duration in a nowrap meta row.
 */
export function formatInstallCountRow(n: number): string {
  return n === 0 ? "NEW" : n.toLocaleString("en-US");
}

/**
 * Accessible name fragment, spelled out so a screen reader never reads an
 * abbreviation: "3,255 installs", "1 install", "not installed yet".
 */
export function installCountAriaLabel(n: number): string {
  if (n === 0) return "not installed yet";
  return `${n.toLocaleString("en-US")} ${n === 1 ? "install" : "installs"}`;
}

/**
 * The plain §02.9 number, with no unit baked in — for callers whose count
 * isn't an install tally (a category's sound count, a search result total,
 * etc.) but still owes the reader the same two guarantees: no abbreviation
 * ("3.5k" loses precision to save two characters) and a real thousands
 * separator. Callers own their own unit text and zero-state copy, since
 * "0 sounds" and "not installed yet" are not the same sentence.
 */
export function formatCatalogCount(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * From 1 to 24 installs a count reads as "almost nobody has used this" rather
 * than anything a visitor can act on, and on the long tail that is most of the
 * catalog. Detail pages and their related rows omit the figure there. Zero is
 * not omitted: it is the first-class state of section 02.9, and 25 and up show
 * the count.
 */
export const INSTALL_COUNT_DISPLAY_FLOOR = 25;

/** The count to print for `n` on a detail page or related row, or null to print none. */
export function visibleInstallCount(n: number): InstallCountDetail | null {
  return n === 0 || n >= INSTALL_COUNT_DISPLAY_FLOOR ? formatInstallCountDetail(n) : null;
}

/**
 * A hub's descriptor line: "410 sounds, 16,697 installs". `floor` marks a
 * capped read, so the sound count gets a "+" and the install total, summed
 * over the capped rows only, is left out rather than understated.
 */
export function formatHubCounts(sounds: number, installs: number, { floor = false } = {}): string {
  const soundPart = `${formatCatalogCount(sounds)}${floor ? "+" : ""} ${sounds === 1 && !floor ? "sound" : "sounds"}`;
  if (floor || installs === 0) return soundPart;
  return `${soundPart}, ${formatCatalogCount(installs)} ${installs === 1 ? "install" : "installs"}`;
}
